// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { bool, number, func, shape, array, object, string } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import uniqueId from 'lodash/uniqueId';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableSkeleton,
} from '@carbon/react';
import { Copy, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowDownUp } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';
import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';
import useToastNotifications from 'Hooks/useToastNotifications';
import ReportsPagination from 'Containers/Reports/ReportsPagination';
import FilterReports from 'Containers/Reports/FilterReports';

const StyledPayments = styled.div`
  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;

    thead th {
      color: #3a3a3a;
      font-size: 16px;
      font-weight: 600;
      background: #f5f7f9;

      .cds--table-header-label {
        font-size: 16px;
        font-weight: 500;
        text-align: center;

        button {
          width: 100%;
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          background: transparent;
          border: none;
          gap: 5px;
          font-size: inherit;
          font-weight: inherit;

          svg {
            width: 18px;
            height: 18px;
            stroke: #707070;
          }
        }
      }
    }

    tbody tr td {
      background-color: #fff;
      text-align: center;

      &[data-label="iban"] {
        display: flex;
        justify-content: center;
        gap: 5px;
        min-height: 48px;
        align-items: center;
      }

      .ibanContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
      }

      .copy {
        cursor: pointer;
        min-width: 16px;
        color: ${ props => props.theme.colors.altOrange };
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;
          border: 1px solid #e2e2e2;
          position: relative;

          td {
            border: transparent;
            text-align: left;

            &[data-label="iban"] {
              justify-content: start;
            }

            &:first-of-type {
              background: #F5F7F9;
              position: relative;
            }

            &:last-of-type {
              height: 50px;
            }

            .ellipsis {
              position: relative;
            }

            .ellipsis:before {
              content: '&nbsp;';
              visibility: hidden;
            }

            .ellipsis span {
              position: absolute;
              left: 0;
              right: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .ibanContainer {
              width: 90%;
              justify-content: start;
            }
          }
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label="date"],
    &[data-label="iban"],
    &[data-label="value"] {    
      display: flex;
      align-items: center;
      height: 40px;
      padding-left: 135px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 130px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    } 
  }
`;

const PaymentsComponent = ({
  invoices,
  loading,
  pagination,
  getInvoices,
  handleMobileOrderChange,
  currentSort,
  defaultSortId,
  sortOptions,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate, formatNumber } = useNumberUtils();
  const toast = useToastNotifications();
  const analytics = useAnalytics();

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);
    analytics.track('awb_code_clipboard');

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'totalWithVat') {
      mappedLabel = 'valueWithVat';
    }
    return t(`pages.${mappedLabel}`);
  };

  const headerData = useMemo(
    () => [
      {
        key: 'date',
        header: t('pages.date'),
      },
      {
        key: 'iban',
        header: t('pages.iban'),
      },
      {
        key: 'value',
        header: t('pages.netValue'),
      }
    ],
    []
  );

  const renderTableHeader = header => {
    const mappedSortingParams = {
      date: 'date',
      iban: 'iban',
      value: 'value',
    };

    const [orderBy, orderDirection] = currentSort.id.split('-');
    const isSorted = orderBy === mappedSortingParams[header.key];
    const isAsc = orderDirection === 'asc';

    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted && currentSort.id !== defaultSortId) {
      sortIcon = isAsc ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />;
      sortDirection = isAsc ? 'desc' : 'none';
    }

    if (isSorted && currentSort.id === defaultSortId) {
      sortIcon = <ArrowDownWideNarrow />;
      sortDirection = 'asc';
    }

    return (
      <button type="button" onClick={ () => handleMobileOrderChange(`${ mappedSortingParams[header.key] }-${ sortDirection }`) }>
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  const renderCell = cell => {
    if (cell.info.header === 'date') {
      return formatDate(cell.value, { timeStyle: undefined });
    }

    if (cell.info.header === 'iban') {
      const uid = uniqueId();
      return cell.value && (
        <>
          <div id={ `tooltip-${ uid }}` } className="ibanContainer ellipsis">
            <span className="ibanCode">{ cell.value }</span>
          </div>
          <ReactTooltip
            className="tooltip"
            anchorId={ `tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <span>{ cell.value }</span>
            ) }
          />
          <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(cell.value) } />
        </>
      );
    }

    if (cell.info.header === 'value') {
      const { value, currency } = cell.value;
      return formatNumber(value, { currency });
    }

    return cell.value;
  };

  return (
    <StyledPayments theme={ theme }>
      <FilterReports
        sortOptions={ sortOptions }
        currentSort={ currentSort }
        handleMobileOrderChange={ handleMobileOrderChange }
        getTableData={ getInvoices }
      />
      { loading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ invoices } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getTableProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { invoices.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  { rows.map(row => (
                    <TableRow key={ row.id }>
                      { row.cells.map(cell => (
                        <StyledTableCell
                          data-label={ cell.info.header }
                          name={ () => mapHeaderToLabel(cell.info.header) }
                          key={ cell.id }
                        >
                          { renderCell(cell) }
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <ReportsPagination loading={ loading } { ...pagination } />
    </StyledPayments>
  );
};

PaymentsComponent.propTypes = {
  invoices: array,
  loading: bool,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
    loading: bool,
  }),
  getInvoices: func,
  handleMobileOrderChange: func,
  currentSort: object,
  defaultSortId: string,
  sortOptions: array,
};

PaymentsComponent.displayName = 'PaymentsComponent';

export default PaymentsComponent;
